import {
  watch,
  nextTick,
  computed,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
  toRef,
  type Ref,
} from 'vue';
import IMask, { type InputMask } from 'imask';

export interface NumberMaskProps {
  modelValue?: string | number | null;
  mask?: string | Record<string, any>;
  unmask?: string | boolean;
  scale?: number | string;
  min?: number | string;
  max?: number | string;
}

function coerceToNumber(value: any): number | undefined {
  if (typeof value === 'string') {
    const num = Number(value.replace(/\s/g, '').replace(/,/g, '.'));
    return isNaN(num) ? undefined : num;
  }
  if (typeof value === 'number') {
    return value;
  }
  return undefined;
}

function getLocaleFormat(lang: string) {
  const l = (lang || 'hu').split('-')[0].toLowerCase();

  if (l === 'en') {
    return { thousandsSeparator: ',', radix: '.', mapToRadix: ['.', ','] };
  }

  return { thousandsSeparator: ' ', radix: ',', mapToRadix: ['.', ','] };
}

export function useNumberMask(
  inputRef: Ref<HTMLInputElement | null>,
  props: NumberMaskProps,
  emit: {
    (event: 'update:modelValue', value: any): void;
    (event: 'input', value: any): void;
  },
) {
  let imask: InputMask<any> | null = null;
  let internalUpdate = false;

  const modelValue = toRef(props, 'modelValue');

  const instance = getCurrentInstance();
  const $app = (instance?.proxy as any)?.$app ?? null;
  const currentLocale = computed(
    () => $app?.i18n?.locale || document.documentElement.lang || 'hu',
  );

  function getMaskValue() {
    if (!imask) {
      return null;
    }
    if (props.unmask === 'typed') {
      const val = imask.typedValue;
      return val === '' || isNaN(val as any) ? null : val;
    }
    if (props.unmask) {
      return imask.unmaskedValue || null;
    }
    return imask.value || null;
  }

  function createMask() {
    const el = inputRef.value;
    if (!el) {
      return;
    }

    const format = getLocaleFormat(currentLocale.value);
    const maskOpts: any = {
      mask: Number,
      thousandsSeparator: format.thousandsSeparator,
      radix: format.radix,
      mapToRadix: format.mapToRadix,
      padFractionalZeros: false,
      normalizeZeros: false,
      autofix: true,
    };

    const scale = coerceToNumber(props.scale);
    if (scale !== undefined) {
      maskOpts.scale = scale;
    }

    const min = coerceToNumber(props.min);
    if (min !== undefined) {
      maskOpts.min = min;
    }

    const max = coerceToNumber(props.max);
    if (max !== undefined) {
      maskOpts.max = max;
    }

    imask = IMask(el, maskOpts);

    updateMaskFromProp(modelValue.value);

    imask.on('accept', () => {
      internalUpdate = true;
      const val = getMaskValue();
      emit('update:modelValue', val);
      emit('input', val);

      nextTick(() => {
        internalUpdate = false;
      });
    });
  }

  function updateMaskFromProp(newVal: any) {
    if (!imask) {
      return;
    }

    if (newVal == null || newVal === '') {
      imask.unmaskedValue = '';
      return;
    }

    if (props.unmask === 'typed') {
      imask.typedValue = coerceToNumber(newVal) ?? 0;
    } else if (props.unmask) {
      const format = getLocaleFormat(currentLocale.value);
      imask.unmaskedValue = String(newVal).replace('.', format.radix);
    } else {
      imask.value = String(newVal);
    }
  }

  watch(modelValue, (newVal) => {
    if (internalUpdate || !imask) {
      return;
    }

    const current = getMaskValue();
    if (newVal === current) {
      return;
    }

    if (
      props.unmask === 'typed' &&
      typeof newVal === 'number' &&
      typeof current === 'number' &&
      Math.abs(newVal - current) < 1e-10
    ) {
      return;
    }

    updateMaskFromProp(newVal);
  });

  watch(currentLocale, () => {
    if (!imask) {
      return;
    }
    const typed = imask.typedValue;
    const format = getLocaleFormat(currentLocale.value);
    imask.updateOptions({
      thousandsSeparator: format.thousandsSeparator,
      radix: format.radix,
      mapToRadix: format.mapToRadix,
    } as any);
    if (typeof typed === 'number' && !isNaN(typed)) {
      imask.typedValue = typed;
    }
  });

  onMounted(createMask);

  onBeforeUnmount(() => {
    if (imask) {
      imask.destroy();
      imask = null;
    }
  });
}
